// Global Instructions Rule Applied!
//
// Per-route wrapper for one Configuration panel: the Configuration AREA gives
// every panel its own nav item / tab / route (module keys 'config-*'), and
// this shell renders just that panel full-bleed — no inner left rail.
//
// The panel registry stays CONFIG_SECTIONS in ConfigurationPage.tsx (single
// source of truth; the legacy combined /configuration routes keep consuming it
// during the transition). The admin gate mirrors ConfigurationPage: fail
// closed on anything but an explicit is_app_admin === true. That gate is
// display-only — the /config/* routes enforce with 403s — but a non-admin can
// deep-link here (menu filtering is display-side too), so the refusal note
// renders instead of a broken panel.

import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Typography } from 'antd'
import type { GlobalConfigsState } from '../../../state_manager/interfaces/global_configs.ts'
import { CONFIG_SECTIONS } from './ConfigurationPage.tsx'

const { Title, Text } = Typography

interface ConfigSectionPageProps {
  /** CONFIG_SECTIONS key of the panel to render. */
  sectionKey: string
}

export default function ConfigSectionPage({ sectionKey }: ConfigSectionPageProps) {
  const isAppAdmin = useSelector(
    (state: { globalConfigs: GlobalConfigsState }) => state.globalConfigs?.is_app_admin === true
  )

  const section = useMemo(
    () => CONFIG_SECTIONS.find((s) => s.key === sectionKey),
    [sectionKey]
  )

  if (!section) {
    return (
      <div className='px-4 py-6'>
        <Text type='secondary'>Unknown configuration section.</Text>
      </div>
    )
  }

  if (section.adminOnly && !isAppAdmin) {
    return (
      <div className='px-4 py-6'>
        <Title level={4} style={{ marginTop: 0 }}>{section.label}</Title>
        <Text type='secondary'>
          This section requires administrator access.
        </Text>
      </div>
    )
  }

  return (
    <div className='h-full overflow-hidden'>
      <div className='w-full h-full flex flex-col bg-white overflow-hidden px-2 sm:px-4 py-2 sm:py-4'>
        {section.render()}
      </div>
    </div>
  )
}
